import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import { useAuthStore } from '@/stores/auth.store';
import type { ManagedProjectOption } from '@/types/report-project-work-item-lite';

const MANAGED_PROJECTS_PAGE_SIZE = 100;

/**
 * Frozen L1：仅返回当前用户担任 projectManager 的 ACTIVE 项目。
 */
export function useManagedProjectsForReport() {
  const user = useAuthStore((state) => state.user);
  const [managedProjects, setManagedProjects] = useState<ManagedProjectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setManagedProjects([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await projectsApi.fetchProjects({
        page: 1,
        pageSize: MANAGED_PROJECTS_PAGE_SIZE,
        status: 'ACTIVE',
      });

      const items: ManagedProjectOption[] = result.items
        .filter((project) => project.projectManagerId === user.id)
        .map((project) => ({
          id: project.id,
          name: project.name,
          projectNo: project.projectNo,
          progress: project.progress ?? 0,
        }));

      setManagedProjects(items);
    } catch (err) {
      const text = err instanceof ApiError ? err.message : '加载负责项目失败';
      setError(text);
      setManagedProjects([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const showProjectWorkItemsSection = managedProjects.length > 0;

  return {
    managedProjects,
    loading,
    error,
    showProjectWorkItemsSection,
    reload: load,
  };
}
